/**
 * also known as "initial" states. See:
 *   https://github.com/reactjs/redux/issues/1743
 */

import {TYPE_ISSUES, TYPE_QUALITY_ISSUES} from './issue-types';
import {STATUS_CLOSED, STATUS_VOID, QUALITY_ISSUES_SORTED_STATUSES} from './status-types';
import {viewTypes} from './view-types';
import {
  COLLECTION_TYPES,
  ISSUE_FILTER_PROPERTIES,
  TEMPLATES_FILTER_PROPERTIES,
  PAGE_PARAMS,
  SORTING_PARAMS,
  TEMPLATES_SORT_PROPERTIES
} from './issue-query-params';
import {ISSUE_ATTRIBUTE_NAMES} from './issue-attribute-names';

// Void and closed status are filtered from issues
const filteredOutStatuses = [STATUS_CLOSED, STATUS_VOID];
const ACTIVE_STATUSES = QUALITY_ISSUES_SORTED_STATUSES.filter(status => !filteredOutStatuses.includes(status));

const sharedPreloadedFilterQueryParamsState = {
  [PAGE_PARAMS.OFFSET]: 0,
  [ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER]: [],
  [ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER]: [],
  [ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]: [],
  [ISSUE_FILTER_PROPERTIES.OWNER_FILTER]: [],
  [ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]: [],
  [ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]: '',
  [ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]: '',
  [ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]: '',
  [ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]: '',
  [ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]: '',
  [ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS]: null,
  [ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER]: [],
  [ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES]: [],
  [ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]: '',
  [ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER]: []
};

// We want to set this object once per issue type and view as it is used when resetting filters in the UI (see `Filter.jsx`)
export const preloadedFilterQueryParamsState = {
  [viewTypes.DOCUMENT]: {
    [TYPE_QUALITY_ISSUES]: {
      ...sharedPreloadedFilterQueryParamsState,
      [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: ACTIVE_STATUSES
    }
  },
  [viewTypes.PROJECT]: {
    [TYPE_QUALITY_ISSUES]: {
      ...sharedPreloadedFilterQueryParamsState,
      [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: []
    },
    [COLLECTION_TYPES.TEMPLATES]: {
      [TEMPLATES_FILTER_PROPERTIES.CATEGORY]: [],
      [TEMPLATES_FILTER_PROPERTIES.SUBTYPE]: [],
      [TEMPLATES_FILTER_PROPERTIES.STATUS]: [],
      [TEMPLATES_FILTER_PROPERTIES.LOCATION]: null,
      [TEMPLATES_FILTER_PROPERTIES.ASSIGNED_TO]: [],
      [TEMPLATES_FILTER_PROPERTIES.OWNER]: [],
      [TEMPLATES_FILTER_PROPERTIES.ROOT_CAUSE]: [],
      [TEMPLATES_FILTER_PROPERTIES.CREATED_BY]: [],
      [TEMPLATES_FILTER_PROPERTIES.UPDATED_BY]: [],
      [TEMPLATES_FILTER_PROPERTIES.CREATED_AT_START]: null,
      [TEMPLATES_FILTER_PROPERTIES.CREATED_AT_END]: null,
      [TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_START]: null,
      [TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_END]: null,
      [TEMPLATES_FILTER_PROPERTIES.IS_ACTIVE]: null
    }
  }
};

export const initialFilterQueryParamStates = {
  ...preloadedFilterQueryParamsState,
  [viewTypes.PROJECT]: {
    ...preloadedFilterQueryParamsState[viewTypes.PROJECT],
    [TYPE_QUALITY_ISSUES]: {
      ...preloadedFilterQueryParamsState[viewTypes.PROJECT][TYPE_QUALITY_ISSUES],
      [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: ACTIVE_STATUSES
    }
  }
};

// Default sorting has no difference between views
export const preloadedSortQueryParamsState = {
  [TYPE_ISSUES]: {
    [SORTING_PARAMS.SORT]: `-${ISSUE_ATTRIBUTE_NAMES.ID}`
  },
  [TYPE_QUALITY_ISSUES]: {
    [SORTING_PARAMS.SORT]: `-${ISSUE_ATTRIBUTE_NAMES.ID}`
  }
};

export const defaultTemplatesSortField = TEMPLATES_SORT_PROPERTIES.CATEGORY;

// define (and export) this composite preloadedState once:
export const preloadedQueryParamsState = {
  [viewTypes.DOCUMENT]: {
    [TYPE_ISSUES]: {
      ...preloadedFilterQueryParamsState[viewTypes.DOCUMENT][TYPE_ISSUES],
      ...preloadedSortQueryParamsState[TYPE_ISSUES]
    },
    [TYPE_QUALITY_ISSUES]: {
      ...preloadedFilterQueryParamsState[viewTypes.DOCUMENT][TYPE_QUALITY_ISSUES],
      ...preloadedSortQueryParamsState[TYPE_QUALITY_ISSUES]
    }
  },
  [viewTypes.PROJECT]: {
    [TYPE_ISSUES]: {
      ...preloadedFilterQueryParamsState[viewTypes.PROJECT][TYPE_ISSUES],
      ...preloadedSortQueryParamsState[TYPE_ISSUES]
    },
    [TYPE_QUALITY_ISSUES]: {
      ...preloadedFilterQueryParamsState[viewTypes.PROJECT][TYPE_QUALITY_ISSUES],
      ...preloadedSortQueryParamsState[TYPE_QUALITY_ISSUES]
    },
    [COLLECTION_TYPES.TEMPLATES]: {
      [SORTING_PARAMS.SORT]: defaultTemplatesSortField,
      [PAGE_PARAMS.OFFSET]: 0,
      ...preloadedFilterQueryParamsState[viewTypes.PROJECT][COLLECTION_TYPES.TEMPLATES]
    }
  }
};
